import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getKpis, getChartMonthwise, getImportBatches,
  getChartAgentwise, getChartStatewise, getChartTopparties, getChartBiltystatus,
  getFilterOptions, getSettings,
} from '../lib/api';
import { formatCurrency, formatDate, getCurrentFY } from '../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, FileSpreadsheet, Clock, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

const PIE_COLORS = ['#6D5EF7', '#2563EB', '#1F9D67', '#D98B1E', '#D64545', '#8A7CFF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

function greetingForHour(hour) {
  if (hour < 12) return 'Good Morning,';
  if (hour < 17) return 'Good Afternoon,';
  return 'Good Evening,';
}

export default function DashboardPage() {
  const { user, isNavAllowed } = useAuth();
  // bia.py enforces bia_dashboard/dashboard permissions server-side too (require_permission),
  // but keep both the fetch AND the render behind this client-side gate as well — fetching
  // unconditionally would still leak data into the network tab response even if the UI hid it.
  const canSeeBIA = isNavAllowed('/boss/bia/dashboard');

  const [kpis, setKpis] = useState(null);
  const [monthData, setMonthData] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState('');

  const [fy, setFy] = useState(getCurrentFY());
  const [fyOptions, setFyOptions] = useState([getCurrentFY()]);
  const [biaCharts, setBiaCharts] = useState({});

  useEffect(() => {
    getSettings().then(r => setCompanyName(r.data?.company_name || '')).catch(() => {});
  }, []);

  useEffect(() => {
    getFilterOptions().then(r => {
      const fetched = r.data.financial_years || [];
      // Always keep the current FY selectable, even before any invoices for
      // it have been imported yet (e.g. right after 1 April).
      const merged = fetched.includes(getCurrentFY()) ? fetched : [getCurrentFY(), ...fetched];
      setFyOptions(merged);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = fy ? { fy } : {};
    const calls = [
      getKpis(params).catch(() => ({ data: {} })),
      getChartMonthwise(params).catch(() => ({ data: { data: [] } })),
      getImportBatches().catch(() => ({ data: { batches: [] } })),
    ];
    if (canSeeBIA) {
      calls.push(
        getChartAgentwise(params).catch(() => ({ data: { data: [] } })),
        getChartStatewise(params).catch(() => ({ data: { data: [] } })),
        getChartTopparties(params).catch(() => ({ data: { data: [] } })),
        getChartBiltystatus(params).catch(() => ({ data: { data: [] } })),
      );
    }
    Promise.all(calls).then(([kRes, mRes, bRes, aRes, sRes, pRes, biRes]) => {
      setKpis(kRes.data);
      setMonthData((mRes.data.data || []).map(d => ({ month: d._id, sales: d.total_sales, count: d.count })));
      setBatches(bRes.data.batches || []);
      if (canSeeBIA) {
        setBiaCharts({
          agentwise: (aRes.data.data || []).map(d => ({ name: d._id || 'Unknown', sales: d.total_sales, count: d.count })),
          statewise: (sRes.data.data || []).map(d => ({ name: d._id || 'Unknown', sales: d.total_sales, count: d.count })),
          topparties: (pRes.data.data || []).map(d => ({ name: d._id || 'Unknown', sales: d.total_sales })),
          biltystatus: (biRes.data.data || []).map(d => ({ name: d._id || 'Unknown', value: d.count })),
        });
      }
      setLoading(false);
    });
  }, [fy, canSeeBIA]);


  const kpiCards = kpis ? [
    { label: 'Total Sales', value: formatCurrency(kpis.total_sales), icon: TrendingUp, color: 'text-green-600 bg-green-50' },
    { label: 'Total Invoices', value: kpis.total_invoices, icon: FileSpreadsheet, color: 'text-blue-600 bg-blue-50', to: '/boss/fa/invoices' },
    { label: 'Pending Bilties', value: kpis.pending_bilties, icon: Clock, color: 'text-yellow-600 bg-yellow-50', to: '/boss/fa/invoices?bilty_status=pending' },
    { label: 'Uploaded Bilties', value: kpis.uploaded_bilties, icon: CheckCircle, color: 'text-green-600 bg-green-50', to: '/boss/fa/invoices?bilty_status=uploaded' },
    { label: 'Missing Invoices', value: kpis.missing_invoices, icon: AlertTriangle, color: 'text-orange-600 bg-orange-50', to: '/boss/fa/missing' },
    { label: 'Cancelled', value: kpis.cancelled_invoices, icon: XCircle, color: 'text-red-600 bg-red-50' },
  ] : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-white rounded-xl animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Greeting — a standalone card, not forced to share a row with
          controls of a totally different visual weight (that pairing was
          the actual bug: a solid navy block next to a slim dropdown, with
          a dead gap swallowing the space between them). Full width, real
          substance via the subtitle, matching the rest of the page's cards. */}
      <div className="rounded-xl px-6 py-5 shadow-sm" style={{ backgroundColor: '#001E4B' }}>
        <p className="text-white/70 text-sm">{greetingForHour(new Date().getHours())}</p>
        <p className="text-white text-3xl font-bold tracking-tight mt-1">{user?.name || 'Admin'}</p>
        <p className="text-white/60 text-sm mt-2">Here's your business overview for {companyName || 'your company'}.</p>
      </div>

      {/* Section label + FY filter — two text-scale elements, a natural pairing */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-boss-text-muted uppercase tracking-wide">Overview</h2>
        <select value={fy} onChange={e => setFy(e.target.value)} className="px-3 py-2 rounded-lg border border-boss-border bg-white text-sm">
          <option value="">All FY</option>
          {fyOptions.map(f => <option key={f} value={f}>FY {f}</option>)}
        </select>
      </div>

      {/* KPI Cards — icon-left, stat-right: reads less sparse than a
          vertical stack, especially with placeholder zero values */}
      <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4">
        {kpiCards.map((kpi) => {
          const Wrapper = kpi.to ? Link : 'div';
          return (
            <Wrapper key={kpi.label} {...(kpi.to ? { to: kpi.to } : {})} className="bg-white rounded-xl p-4 shadow-sm border border-boss-border hover:shadow-md transition-shadow flex items-center gap-3" data-testid={`kpi-${kpi.label.replace(/\s/g, '-').toLowerCase()}`}>
              <div className={`flex items-center justify-center w-11 h-11 rounded-lg shrink-0 ${kpi.color}`}>
                <kpi.icon size={20} />
              </div>
              <div className="min-w-0">
                <div className="text-2xl font-bold text-boss-text leading-tight truncate">{kpi.value}</div>
                <div className="text-xs text-boss-text-muted mt-0.5 truncate">{kpi.label}</div>
              </div>
            </Wrapper>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-boss-border">
          <h3 className="text-sm font-semibold text-boss-text mb-4">Monthly Sales Trend</h3>
          {monthData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F7" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis fontSize={12} tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="sales" fill="#6D5EF7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-60 flex items-center justify-center text-boss-text-muted">No data yet. Import invoices to see trends.</div>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
          <h3 className="text-sm font-semibold text-boss-text mb-4">Recent Imports</h3>
          {batches.length > 0 ? (
            <div className="space-y-3">
              {batches.slice(0, 6).map((b) => (
                <div key={b._id} className="flex items-center gap-3 py-2 border-b border-boss-border last:border-0">
                  <div className={`w-2 h-2 rounded-full ${b.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-boss-text truncate">{b.file_name}</div>
                    <div className="text-xs text-boss-text-muted">{b.total_rows} rows | {formatDate(b.upload_time)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-boss-text-muted text-sm">No imports yet</div>
          )}
        </div>
      </div>

      {/* Business Intelligence — gated: see canSeeBIA note above */}
      {canSeeBIA && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
            <h3 className="text-sm font-semibold text-boss-text mb-4">Agent-wise Sales</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={biaCharts.agentwise || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F7" />
                <XAxis type="number" fontSize={12} tickFormatter={v => formatCurrency(v)} />
                <YAxis type="category" dataKey="name" fontSize={10} width={100} />
                <Tooltip formatter={v => formatCurrency(v)} />
                <Bar dataKey="sales" fill="#2563EB" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
            <h3 className="text-sm font-semibold text-boss-text mb-4">State-wise Sales</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={biaCharts.statewise || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F7" />
                <XAxis type="number" fontSize={12} tickFormatter={v => formatCurrency(v)} />
                <YAxis type="category" dataKey="name" fontSize={10} width={80} />
                <Tooltip formatter={v => formatCurrency(v)} />
                <Bar dataKey="sales" fill="#1F9D67" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
            <h3 className="text-sm font-semibold text-boss-text mb-4">Top 10 Parties by Revenue</h3>
            <div className="space-y-2">
              {(biaCharts.topparties || []).map((p, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-boss-violet/10 flex items-center justify-center text-xs font-semibold text-boss-violet">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="w-full bg-boss-surface-3 rounded-full h-1.5 mt-1">
                      <div className="bg-boss-violet h-1.5 rounded-full" style={{ width: `${Math.min(100, (p.sales / (biaCharts.topparties?.[0]?.sales || 1)) * 100)}%` }} />
                    </div>
                  </div>
                  <span className="text-sm font-mono font-medium">{formatCurrency(p.sales)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
            <h3 className="text-sm font-semibold text-boss-text mb-4">Bilty Status Distribution</h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={biaCharts.biltystatus || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, value }) => `${name}: ${value}`}>
                  {(biaCharts.biltystatus || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
